import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { sanitizeHTML } from '../utils/sanitize';

const Inscription = () => {
  const weTravelContainerRef = useRef(null);

  // Charger le script WeTravel pour le widget button
  useEffect(() => {
    // Charger la police Poppins pour WeTravel
    const existingFont = document.getElementById('poppins_font');
    if (!existingFont) {
      const fontLink = document.createElement('link');
      fontLink.id = 'poppins_font';
      fontLink.href = 'https://fonts.googleapis.com/css?family=Poppins';
      fontLink.rel = 'stylesheet';
      document.head.appendChild(fontLink);
    }

    // Fonction pour initialiser manuellement le bouton WeTravel
    // basé sur le code source de embed_checkout.js
    const initWeTravelButton = () => {
      const buttons = document.querySelectorAll('.wtrvl-checkout_button');
      buttons.forEach((button) => {
        if (button.dataset.initialized) return;
        button.dataset.initialized = 'true';
        
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          
          // Créer l'iframe de checkout
          const iframe = document.createElement('iframe');
          const href = button.getAttribute('href');
          const url = new URL(href);
          url.searchParams.set('source', 'widget_book_now');
          
          iframe.setAttribute('style', 'position:fixed;width:100vw;height:100vh;height:100dvh;top:0;left:0;bottom:0;right:0;z-index:21150313555');
          iframe.setAttribute('frameborder', '0');
          iframe.setAttribute('src', url.toString());
          iframe.setAttribute('class', 'wtrvl-ifrm');
          
          // Ajouter un style pour masquer le reste du contenu sur mobile
          const style = document.createElement('style');
          style.id = 'wtrvl-style';
          if (window.innerWidth <= 991) {
            style.innerHTML = 'body > *:not(.wtrvl-ifrm){display:none}';
            document.body.appendChild(style);
          }
          
          document.body.appendChild(iframe);
          document.body.style.setProperty('overflow', 'hidden', 'important');
          document.documentElement.style.setProperty('overflow', 'hidden', 'important');
          
          // Envoyer un message à l'iframe après chargement
          setTimeout(() => {
            iframe.contentWindow.postMessage('buttonWidget', 'https://www.wetravel.com');
          }, 3000);
        });
      });
      
      // Écouter le message de fermeture de l'iframe
      const handleMessage = (event) => {
        if (event.data === 'wtrvlCheckoutClosed') {
          const iframe = document.querySelector('.wtrvl-ifrm');
          const style = document.getElementById('wtrvl-style');
          if (iframe) iframe.remove();
          if (style) style.remove();
          document.body.style.removeProperty('overflow');
          document.documentElement.style.removeProperty('overflow');
        }
      };
      
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    };

    // Initialiser après un court délai pour s'assurer que le DOM est prêt
    const timeout = setTimeout(() => {
      initWeTravelButton();
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const cycles = [
    {
      id: 1,
      title: "Le Cycle Initiatique Intégral",
      subtitle: "Préparation · Immersion · Intégration",
      dates: "25 juillet — 31 octobre 2026",
      price: "1 888 €",
      description: "L'expérience complète.\nLe processus dans sa continuité.",
      highlight: true,
      link: "https://tri.ps/MBpMj",
      wetravel: {
        uuid: "4599176313",
        buttonColor: "#e4bb76",
        textColor: "#ffffff",
        buttonText: "Je choisis ce cycle"
      }
    },
    {
      id: 2,
      title: "Préparation & Traversée",
      subtitle: "Pré-COCON + Immersion à Valouse",
      dates: "25 juillet — 11 septembre 2026",
      price: "1 688 €",
      description: "Ancrer avant d'entrer.\nTraverser pleinement.",
      highlight: false,
      link: "https://tri.ps/NxTzZ",
      wetravel: {
        uuid: "9319532325",
        buttonColor: "#b7afa2"
      }
    },
    {
      id: 3,
      title: "Traversée & Intégration",
      subtitle: "Immersion à Valouse + Post-COCON",
      dates: "7 septembre — 31 octobre 2026",
      price: "1 688 €",
      description: "Vivre.\nPuis intégrer dans la matière.",
      highlight: false,
      link: "https://tri.ps/gZcgk",
      wetravel: {
        uuid: "1950533945",
        buttonColor: "#ab9d90",
        buttonText: "Je réserve ce cycle"
      }
    },
    {
      id: 4,
      title: "La Traversée Centrale",
      subtitle: "Immersion à Valouse",
      dates: "7 — 11 septembre 2026",
      price: "1 488 €",
      description: "Le cœur du cycle.\nL'expérience à Valouse.",
      highlight: false,
      link: "https://tri.ps/HZJjS",
      wetravel: {
        uuid: "4257488537",
        buttonColor: "#d5c1a9",
        buttonText: "Je choisis ce cycle"
      }
    },
  ];

  return (
    <div className="min-h-screen bg-[#F3E5CF]">
      {/* Header immersif */}
      <div className="relative h-[50vh] md:h-[60vh] overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_98f24ef8-b599-4c48-83d5-8100dc89f188/artifacts/3ikzh477_DSC05787.jpg"
          alt="Hameau de Valouse"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#F3E5CF]" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-[#3F2A24]">
            <h1 className="font-serif text-4xl md:text-6xl tracking-wide drop-shadow-[0_2px_4px_rgba(255,255,255,0.8)]">
              COCON–VALOUSE
            </h1>
            <p className="mt-8 text-lg md:text-xl tracking-[0.2em] uppercase drop-shadow-[0_2px_4px_rgba(255,255,255,0.8)]">
              2026
            </p>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        
        {/* Introduction */}
        <div className="text-center mb-16 space-y-4">
          <p className="text-[#3F2A24] text-lg">
            Choisis ton cycle.
          </p>
          <p className="text-[#3F2A24]/70 text-sm">
            Chaque formule est une porte d'entrée.<br />
            Le cercle reste le même.
          </p>
        </div>

        {/* Les 4 espaces */}
        <div className="space-y-12">
          {cycles.map((cycle) => (
            <div
              key={cycle.id}
              className={`p-8 md:p-12 rounded-3xl transition-all ${
                cycle.highlight
                  ? 'bg-[#C5A059] text-[#3F2A24]'
                  : 'bg-white/60 text-[#3F2A24] border border-[#C5A059]/30'
              }`}
            >
              <div className="text-center space-y-4">
                {/* Titre principal */}
                <p className="text-xs tracking-[0.3em] uppercase opacity-70">
                  COCON — VALOUSE 2026
                </p>
                
                {/* Titre du cycle */}
                <h2 className="font-serif text-2xl md:text-3xl">
                  {cycle.title}
                </h2>
                
                {/* Sous-titre */}
                <p className="text-sm tracking-[0.15em]">
                  {cycle.subtitle}
                </p>
                
                {/* Dates */}
                <p className="text-sm opacity-80">
                  {cycle.dates}
                </p>
                
                {/* Prix */}
                <p className="font-bold text-3xl md:text-4xl pt-4">
                  {cycle.price}
                </p>
                
                {/* Description */}
                <p className="text-sm whitespace-pre-line pt-2 opacity-90">
                  {cycle.description}
                </p>
                
                {/* Bouton */}
                <div className="pt-6">
                  {cycle.wetravel ? (
                    <div 
                      dangerouslySetInnerHTML={sanitizeHTML(`<div style="text-align: center;"><button class="wtrvl-checkout_button" id="wetravel_button_cycle_${cycle.id}" data-env="https://www.wetravel.com" data-version="v0.4" data-uid="1221488" data-uuid="${cycle.wetravel.uuid}" href="https://www.wetravel.com/checkout_embed?uuid=${cycle.wetravel.uuid}" style="background-color:${cycle.wetravel.buttonColor};color:${cycle.wetravel.textColor || '#313738'};border: 0px;border-radius: 5px;font-family: 'Poppins', sans-serif;font-weight: 400;font-size: 14px;-webkit-font-smoothing: antialiased;text-transform: capitalize;padding: 13px 24px;text-decoration: none;text-align: center;line-height: 14px;display: inline-block; cursor: pointer;">${cycle.wetravel.buttonText || 'Je choisis ce cycle'}</button></div>`)}
                    />
                  ) : (
                    <a
                      href={cycle.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-block px-8 py-4 rounded-full uppercase tracking-widest text-xs transition-colors ${
                        cycle.highlight
                          ? 'bg-[#3F2A24] text-[#F3E5CF] hover:bg-[#56433e]'
                          : 'bg-[#3F2A24] text-[#F3E5CF] hover:bg-[#56433e]'
                      }`}
                      data-testid={`cycle-${cycle.id}-btn`}
                    >
                      Je choisis ce cycle
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Retour */}
        <div className="text-center mt-16">
          <Link
            to="/valouse"
            className="text-[#3F2A24]/60 text-sm hover:text-[#3F2A24] transition-colors"
          >
            ← Retour à la page COCON Valouse
          </Link>
        </div>

      </div>
    </div>
  );
};

export default Inscription;
